import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@shared/supabase/client';
import { captureError, setUserContext } from '@shared/lib/observability';

export type Role = 'public' | 'precustomer' | 'customer' | 'creator' | 'field_agent' | 'admin';
export type AccessStatus = 'pending' | 'active' | 'suspended';

interface AuthState {
  user: User | null;
  role: Role;
  /** Account approval state (Access Control Phase 1). null until loaded. */
  accessStatus: AccessStatus | null;
  /** Feature keys the user may use (from the JWT app_metadata claim). Empty until loaded. */
  features: Set<string>;
  /** True if the user may use feature `key` (admins bypass). */
  hasFeature: (key: string) => boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  role: 'public',
  accessStatus: null,
  features: new Set(),
  hasFeature: () => false,
  loading: true,
  signOut: async () => {},
});

// Rank for picking the primary role when a user has multiple.
// Matches RoleGuard's RANK table: admin > field_agent/creator/customer > precustomer > public.
const ROLE_RANK: Record<Role, number> = {
  public: 0, precustomer: 0, customer: 1, creator: 1, field_agent: 1, admin: 3,
};

type Identity = { role: Role; access: AccessStatus | null; features: Set<string> };

function b64url(s: string): string {
  const b = s.replace(/-/g, '+').replace(/_/g, '/');
  return atob(b + '='.repeat((4 - (b.length % 4)) % 4));
}

// Read role/access/features from the JWT app_metadata claim injected by
// custom_access_token_hook — no DB round-trips. Returns features:null when the
// token predates the hook (no features claim) so the caller falls back to RPC.
function claimsFromToken(accessToken: string | undefined): { role: Role; access: AccessStatus | null; features: Set<string> | null } {
  if (!accessToken) return { role: 'public', access: null, features: new Set() };
  try {
    const payload = JSON.parse(b64url(accessToken.split('.')[1] ?? '')) as { app_metadata?: Record<string, unknown> };
    const am = payload.app_metadata ?? {};
    const rawRole = String(am.role ?? '');
    const role = (rawRole in ROLE_RANK ? rawRole : 'precustomer') as Role;
    const access = (am.access as AccessStatus | undefined) ?? null;
    const features = Array.isArray(am.features) ? new Set<string>(am.features as string[]) : null;
    return { role, access, features };
  } catch {
    return { role: 'public', access: null, features: new Set() };
  }
}

// Fallback for tokens issued before the hook was enabled: read role + access + features
// from the DB the old way. Removable once every session has refreshed past 2026-07-25.
async function resolveViaRpc(userId: string): Promise<Identity> {
  const [{ data: roleRows }, { data: prof }, { data: feats }] = await Promise.all([
    supabase.from('user_roles').select('role').eq('user_id', userId),
    supabase.from('profiles').select('access_status').eq('id', userId).maybeSingle(),
    supabase.rpc('my_features'),
  ]);
  const ranked = ((roleRows ?? []) as { role: string }[])
    .map((r) => r.role as Role).filter((r): r is Role => r in ROLE_RANK)
    .sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a]);
  return {
    role: ranked[0] ?? 'precustomer',
    access: ((prof as { access_status?: string } | null)?.access_status ?? null) as AccessStatus | null,
    features: new Set<string>(((feats ?? []) as { feature_key: string }[]).map((f) => f.feature_key)),
  };
}

// Token-first identity resolution; RPC fallback only for pre-hook tokens.
async function resolveIdentity(session: { access_token?: string; user?: User | null } | null): Promise<Identity> {
  const c = claimsFromToken(session?.access_token);
  if (c.features) return { role: c.role, access: c.access, features: c.features };
  const uid = session?.user?.id;
  if (!uid) return { role: 'public', access: null, features: new Set() };
  return resolveViaRpc(uid);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>('public');
  const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
  const [features, setFeatures] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  // Track the current user id to skip setUser / loadRole when SIGNED_IN fires
  // with the same session (happens on every tab focus via visibilitychange →
  // getSession()). Without this guard, every tab switch re-renders the entire
  // auth context and can reset in-progress form state (e.g. the Add blogger dialog).
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    let settled = false;
    const finish = () => {
      if (!active || settled) return;
      settled = true;
      setLoading(false);
    };

    // Hard timeout: never keep the app on "Loading…" longer than 3s.
    // With the navigator.locks override in client.ts, getSession() is now
    // localStorage-only and should settle in <10ms, so 3s is very generous —
    // if we hit this path it means something is genuinely broken (CSP, SW,
    // or env misconfig) and we want to fail fast instead of staring at a
    // blank screen.
    const timeout = setTimeout(() => {
      if (!settled) {
        // eslint-disable-next-line no-console
        console.warn('[auth] getSession timed out; proceeding as unauthenticated');
        finish();
      }
    }, 3000);

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!active) return;
        const u = data.session?.user ?? null;
        currentUserIdRef.current = u?.id ?? null;
        setUser(u);
        setUserContext(u ? { id: u.id, email: u.email ?? null } : null);
        if (u) {
          try {
            const id = await resolveIdentity(data.session);
            setRole(id.role);
            setAccessStatus(id.access);
            setFeatures(id.features);
          } catch (e) { captureError(e, { where: 'auth.resolveIdentity.initial' }); }
        }
      } catch (e) {
        captureError(e, { where: 'auth.getSession' });
      } finally {
        clearTimeout(timeout);
        finish();
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      const u = session?.user ?? null;
      const newId = u?.id ?? null;

      // SIGNED_IN fires on every tab focus (visibilitychange → getSession()).
      // Skip the full re-render + loadRole when it's the same user — we already
      // have the correct role from the initial load or a previous TOKEN_REFRESHED.
      if (event === 'SIGNED_IN' && newId && newId === currentUserIdRef.current) return;

      currentUserIdRef.current = newId;
      setUser(u);
      setUserContext(u ? { id: u.id, email: u.email ?? null } : null);
      if (!u) {
        setRole('public');
        setAccessStatus(null);
        setFeatures(new Set());
        return;
      }
      try {
        const id = await resolveIdentity(session);
        // Don't clobber a good role with 'public' on a transient error.
        if (id.role !== 'public') setRole(id.role);
        setAccessStatus(id.access);
        setFeatures(id.features);
      } catch (e) {
        captureError(e, { where: 'auth.resolveIdentity.onChange', event });
        // Leave state as-is; don't downgrade mid-session.
      }
    });
    return () => {
      active = false;
      clearTimeout(timeout);
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    currentUserIdRef.current = null;
    setUser(null);
    setRole('public');
    setAccessStatus(null);
    setFeatures(new Set());
  }, []);

  const hasFeature = useCallback(
    (key: string) => role === 'admin' || features.has(key),
    [role, features],
  );

  const ctxValue = useMemo(
    () => ({ user, role, accessStatus, features, hasFeature, loading, signOut }),
    [user, role, accessStatus, features, hasFeature, loading, signOut],
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
